import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

type UpgradeModalProps = {
  onClose: () => void;
};

const MusicNoteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
  </svg>
);

const FeatureIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="upgrade-modal-feature-icon" aria-hidden="true">{children}</span>
);

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ onClose }) => {
  const navigate = useNavigate();

  const handleViewPlans = useCallback(() => {
    onClose();
    navigate("/subscription");
  }, [onClose, navigate]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div className="upgrade-modal-overlay" onClick={handleOverlayClick}>
      <div className="upgrade-modal upgrade-modal-wide" role="dialog" aria-modal="true" aria-labelledby="upgrade-modal-title">
        <button
          className="upgrade-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="upgrade-modal-early-access-badge">Early Access</div>

        <h2 id="upgrade-modal-title" className="upgrade-modal-title">
          <span className="upgrade-modal-crown-wrap"><MusicNoteIcon /> Fuwalo Star</span> Pro Plans
        </h2>
        <p className="upgrade-modal-subtitle">
          Fuwalo Star is currently in <strong>early access</strong> &mdash; all features are free during this period.
          These plans show what&apos;s coming when we launch.
        </p>

        <div className="upgrade-modal-plans">
          <div className="upgrade-modal-plan">
            <h3>Standard</h3>
            <p className="upgrade-modal-plan-price">$9<span>/month</span></p>
            <p className="upgrade-modal-plan-coming-soon">Coming soon</p>
            <ul>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                20 transcriptions/month
              </li>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                Sheet music export
              </li>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                Up to 30 min per file
              </li>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                Adjustable sensitivity
              </li>
            </ul>
          </div>

          <div className="upgrade-modal-plan upgrade-modal-plan-featured">
            <div className="upgrade-modal-plan-badge">Best Value</div>
            <h3>Pro</h3>
            <p className="upgrade-modal-plan-price">$15<span>/month</span></p>
            <p className="upgrade-modal-plan-coming-soon">Coming soon</p>
            <ul>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                45 transcriptions/month
              </li>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                Sheet music export
              </li>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                Batch processing
              </li>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                Up to 1 hour per file
              </li>
              <li>
                <FeatureIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </FeatureIcon>
                Priority processing
              </li>
            </ul>
          </div>
        </div>

        <div className="upgrade-modal-actions">
          <button className="upgrade-modal-cta" onClick={handleViewPlans}>
            <MusicNoteIcon />
            View Plans
          </button>
          <p className="upgrade-modal-trial-note">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Everything is free during early access &mdash; no payment required
          </p>
        </div>
      </div>
    </div>
  );
};
